
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Layout from '../../../../components/docs/Layout';
export const metadata = {
  toc: true,
  title: 'Authentication',
  section: 'Authentication',
  layout: 'reference',
  description: 'Building Plaid Exchange OAuth',
  maxTocLevel: 3
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const AggregatorSchema = makeShortcode("AggregatorSchema");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Authentication' subtitle={metadata.description} mdxType="Header"></Header>
    <h2 {...{
      "id": "overview"
    }}>{`Overview`}</h2>
    <p>{`This section will help you prepare your OAuth 2.0 provider for connecting to Plaid.`}</p>
    <h3 {...{
      "id": "prepare-your-oauth-20-server"
    }}>{`Prepare your OAuth 2.0 server`}</h3>
    <p>{`If you are using your existing OAuth identity provider. (For example, Okta, Auth0, Ping Identity, Azure Active Directory, AWS Cognito), please prepare all the URLs and settings required for access. This include firewall rules, scopes and audiences. Initially have these URLs ready:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`End Point`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`authorization_endpoint`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Your OAuth landing page, where users can sign in and generate a `}<inlineCode parentName="td">{`code`}</inlineCode>{` for Plaid`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`token_endpoint`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The token exchange endpoint where Plaid can use the `}<inlineCode parentName="td">{`code`}</inlineCode>{` to generate an `}<inlineCode parentName="td">{`id_token`}</inlineCode>{`, `}<inlineCode parentName="td">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="td">{`refresh_token`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <Callout mdxType="Callout">
      <p>{`If you don't yet have an identity provider, Plaid recommends `}<a parentName="p" {...{
          "href": "https://www.okta.com/blog/2023/02/how-oktas-new-plaid-integration-keeps-your-banking-secure/"
        }}>{`our partner, Okta`}</a>{`. Okta is an industry-leading, independent provider with expertise onboarding data partners to OAuth integrations. `}<a parentName="p" {...{
          "href": "mailto:dataconnectivity@plaid.com"
        }}>{`Contact us`}</a>{` for help getting started with Okta.`}</p>
      <p>{`If these are not options, there is the option of building a net new provider to support Plaid Exchange.`}</p>
    </Callout>
    <p>{`Note: OIDC Discovery (Well Known Config) and JWKS are not supported in Plaid Exchange`}</p>
    <h5 {...{
      "id": "issue-plaid-a-client-id-and-client-secret"
    }}>{`Issue Plaid a client ID and client secret`}</h5>
    <p>{`In order to allow Plaid to authenticate its request for an access token, you will need to issue a client ID and client secret.
You may create the client ID and client secret using your preferred method, but we provide some guidance `}<a parentName="p" {...{
        "href": "#client-id-and-client-secret"
      }}>{`in this guide`}</a>{`.
Once you have created a client ID and client secret, provide them to your Plaid contact.`}</p>
    <h5 {...{
      "id": "authorization-flow-overview"
    }}>{`Authorization flow overview`}</h5>
    <p>{`Once you have a server and have issued Plaid a client ID and client secret, the authorization flow occurs as follows:`}</p>
    <ol>
      <li parentName="ol">{`Plaid redirects the end user to your `}<a parentName="li" {...{
          "href": "#plaid-redirects-the-end-user"
        }}><inlineCode parentName="a">{`authorization_endpoint`}</inlineCode></a>{`.`}</li>
      <li parentName="ol">{`The user completes all authentication steps and you generate an `}<a parentName="li" {...{
          "href": "#you-give-plaid-an-authorization-code"
        }}>{`authorization code`}</a>{`.`}</li>
      <li parentName="ol">{`Plaid uses the authorization code to request an `}<a parentName="li" {...{
          "href": "#token-flow"
        }}>{`access token`}</a>{`.`}</li>
      <li parentName="ol">{`Plaid uses the access token to identify the user (unique consistency key).`}</li>
    </ol>
    <p>{`(This described authentication flow conforms to commonly implemented patterns for the `}<a parentName="p" {...{
        "href": "https://openid.net/specs/"
      }}>{`OIDC spec`}</a>{`. Plaid welcomes partner `}<a parentName="p" {...{
        "href": "mailto:dataconnectivity@plaid.com"
      }}>{`feedback`}</a>{`.)`}</p>
    <h5 {...{
      "id": "client-id-and-client-secret"
    }}>{`Client ID and client secret`}</h5>
    <p>{`In order to register an application for your server, you will need to create a client ID and client secret.
These will be shared with Plaid so that the Plaid can identify itself.
Below, you will find best practices and code samples to help you create a `}<a parentName="p" {...{
        "href": "#client-id"
      }}>{`client ID`}</a>{` and `}<a parentName="p" {...{
        "href": "#client-secret"
      }}>{`client secret`}</a>{`.`}</p>
    <h5 {...{
      "id": "client-id"
    }}>{`Client ID`}</h5>
    <p>{`A client ID is a public identifier that you assign to Plaid.
While it is not secret, we recommend choosing a client ID that is not easy to guess.`}</p>
    <h5 {...{
      "id": "how-to-create-a-client-id"
    }}>{`How to create a client ID`}</h5>
    <p>{`One way to create a client ID is to use a random 32-character hex string.
See the code samples below:`}</p>
    <MDXCodeBlock type="server" title="Generate Client ID" mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`openssl rand -hex 16
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-javascript"
        }}>{`import crypto from 'crypto';
const randomString = crypto.randomBytes(16).toString('hex');
console.log(randomString);
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import secrets
secrets.token_hex(16)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`require('crypto').randomBytes(16).toString('hex');
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import java.security.SecureRandom;
public class Main {
    public static void main(String[] args) {
        byte[] b = new byte[16];
        new SecureRandom().nextBytes(b);
        System.out.printf("%064x%n", new java.math.BigInteger(1, b));
    }
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`puts SecureRandom.hex(16)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`package main
import (
    "crypto/rand"
    "fmt"
)
func main() {
    b := make([]byte, 16)
    rand.Read(b)
    fmt.Printf("%x\\n", b)
}
`}</code></pre>
    </MDXCodeBlock>
    <h4 {...{
      "id": "client-secret"
    }}>{`Client secret`}</h4>
    <p>{`The client secret is essentially a password that you assign to Plaid.
In order to keep it secure, please follow these `}<a parentName="p" {...{
        "href": "https://www.oauth.com/oauth2-servers/client-registration/client-id-secret/"
      }}>{`best practices`}</a>{`:`}</p>
    <ul>
      <li parentName="ul">{`Generate the client secret in a way that makes it impossible to guess or backwards-generate. (For example, do not use a UUID as many common libraries take into account the timestamp or MAC address of the generating server).`}</li>
      <li parentName="ul">{`Never store the client secret in plain text—always keep it encrypted or hashed.`}</li>
      <li parentName="ul">{`Make the client secret visually different from the client ID. (This reduces the risk of a user copy/paste error when handling the client ID and client secret).`}</li>
    </ul>
    <h4 {...{
      "id": "how-to-generate-a-client-secret"
    }}>{`How to generate a client secret`}</h4>
    <p>{`One way to securely generate a client secret is to follow the steps below:`}</p>
    <ol>
      <li parentName="ol">{`Create a 256-bit value using a cryptographically secure pseudo random number generator (CSPRNG).`}</li>
      <li parentName="ol">{`Convert the value from step 1 to a hexadecimal representation.`}</li>
    </ol>
    <p>{`See the code samples below:`}</p>
    <MDXCodeBlock type="server" title="Generate Client Secret" mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`openssl rand -hex 32
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-javascript"
        }}>{`import crypto from 'crypto';
const randomString = crypto.randomBytes(32).toString('hex');
console.log(randomString);
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import secrets
secrets.token_hex(32)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`require('crypto').randomBytes(32).toString('hex');
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import java.security.SecureRandom;
public class Main {
    public static void main(String[] args) {
        byte[] b = new byte[32];
        new SecureRandom().nextBytes(b);
        System.out.printf("%064x%n", new java.math.BigInteger(1, b));
    }
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`puts SecureRandom.hex(32)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`package main
import (
    "crypto/rand"
    "fmt"
)
func main() {
    b := make([]byte, 32)
    rand.Read(b)
    fmt.Printf("%x\\n", b)
}
`}</code></pre>
    </MDXCodeBlock>
    <h2 {...{
      "id": "authorization-flow"
    }}>{`Authorization flow`}</h2>
    <h4 {...{
      "id": "plaid-redirects-the-end-user"
    }}>{`Plaid redirects the end user`}</h4>
    <p>{`When your user first initiates the process of linking their account, Plaid redirects their browser to the `}<inlineCode parentName="p">{`authorization_endpoint`}</inlineCode>{`. Plaid's redirect includes these query parameters:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Query parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Value`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`response_type`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`code`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The type of response Plaid expects.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`redirect_uri`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`https://cdn.plaid.com/link/v2/stable/oauth.html`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Where Plaid expects your organization to redirect back to once the user completed all authentication steps.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`client_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A `}<a parentName="td" {...{
              "href": "#client-id-and-client-secret"
            }}>{`client ID`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<a parentName="td" {...{
              "href": "#client-id-and-client-secret"
            }}>{`client ID`}</a>{` you issued to Plaid.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`state`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An opaque string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Plaid generates this. Your organization will return the same string when redirecting to the `}<inlineCode parentName="td">{`redirect_uri`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`institution_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`ID representing the data partner/FI`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`This is a Plaid assigned ID that represents your organization`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`application_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`ID representing the requesting application`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A Plaid assigned ID that represent the application requesting access. Can be used in combination with Permissions Management.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`scope`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`(OPTIONAL) Set of scope string: `}<inlineCode parentName="td">{`http://plaid.test`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The set of scopes Plaid requests access to.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`audience`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`(OPTIONAL) Set of audience strings: `}<inlineCode parentName="td">{`openid offline_access customScope`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The set of audiences Plaid requests access to.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`code_challenge`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`(OPTIONAL) Challenge string`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Provides the code assigned to Plaid to start PKCE flow. Generated with `}<inlineCode parentName="td">{`code_verifier`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`code_challenge_method`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`(OPTIONAL) Defines the PKCE method :`}<inlineCode parentName="td">{`S256`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Provides the PKCE method.`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Optional fields can be requested, please contact Plaid to learn more.`}</p>
    <h4 {...{
      "id": "example"
    }}>{`Example`}</h4>
    <p>{`Plaid's redirect:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`https://auth.firstplatypus.com/oauth2/v1/authorize?response_type=code&redirect_uri=https%3A%2F%2Fcdn.plaid.com%2Flink%2Fv2%2Fstable%2Foauth.html&scope=openid%20offline_access&client_id=c5a5245b062bf8420d11ab4361b28a15&state=eyJvYXV0aF9zdGF0ZV
`}</code></pre>
    <p>{`This page will be requested directly by the user's device. Your authorization endpoint must support TLS and be publicly accessible. Control of user authentication has now been handed off to you. A typical authentication flow will include a username and password submission form and a 2FA step. For partners with native mobile applications, Plaid strongly recommends enabling support for `}<a parentName="p" {...{
        "href": "https://openid.net/tag/app2app/"
      }}>{`App2App`}</a>{` and using `}<a parentName="p" {...{
        "href": "https://webauthn.guide/"
      }}>{`biometric authentication`}</a>{` to improve the user's authentication experience.`}</p>
    <h4 {...{
      "id": "you-give-plaid-an-authorization-code"
    }}>{`You give Plaid an authorization code`}</h4>
    <p>{`After the user completes all required authentication steps, your organization generates a temporary authorization code and redirects the user's browser back to the `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{`. The following query parameters must be included with the request:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Query parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`code`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The temporary authorization code. Plaid exchanges this for an access token in the `}<a parentName="td" {...{
              "href": "#token-flow"
            }}>{`next step`}</a>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`state`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<inlineCode parentName="td">{`state`}</inlineCode>{` parameter from the `}<a parentName="td" {...{
              "href": "#plaid-redirects-the-end-user"
            }}>{`previous step`}</a>{`. Plaid verifies that the two values match.`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "auth-example"
    }}>{`Auth Example`}</h4>
    <p>{`Your response:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false # CURL example",
        "switcher": "false",
        "#": true,
        "CURL": true,
        "example": true
      }}>{`https://cdn.plaid.com/link/v2/stable/oauth.html?code=1284918391&state=eyJvYXV0aF9zdGF0ZV
`}</code></pre>
    <Callout warning mdxType="Callout">
      <p>{`If a user does not complete all the required steps (for example, if they choose to cancel rather than authorize)
you should handle it as an error.
See the `}<a parentName="p" {...{
          "href": "#error-handling"
        }}>{`error handling`}</a>{` section for more information.`}</p>
    </Callout>
    <h2 {...{
      "id": "token-flow"
    }}>{`Token Flow`}</h2>
    <p>{`Plaid sends a request to your `}<inlineCode parentName="p">{`token_endpoint`}</inlineCode>{`.
Unlike the two previous steps, the request to `}<inlineCode parentName="p">{`token_endpoint`}</inlineCode>{` is a backend-to-backend call and must be authenticated. The authentication credentials consist of the `}<a parentName="p" {...{
        "href": "#client-id-and-client-secret"
      }}>{`client ID and client secret`}</a>{` you issued to Plaid.
The authentication method will be one of the `}<a parentName="p" {...{
        "href": "https://www.rfc-editor.org/rfc/rfc6749#section-2.3"
      }}>{`client authentication methods`}</a>{` specified in your `}<a parentName="p" {...{
        "href": "https://openid.net/specs/openid-connect-discovery-1_0.html"
      }}>{`well-known configuration`}</a>{`.`}</p>
    <p>{`For example, if the authentication method is set to `}<inlineCode parentName="p">{`client_secret_basic`}</inlineCode>{`, Plaid will include a `}<a parentName="p" {...{
        "href": "https://www.rfc-editor.org/rfc/rfc6749#section-2.3.1"
      }}>{`basic authentication`}</a>{` header in its request.`}</p>
    <p>{`Plaid will also send the authorization code (`}<inlineCode parentName="p">{`code`}</inlineCode>{`) in the body of the request and expects to receive an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, `}<inlineCode parentName="p">{`id_token`}</inlineCode>{`, and `}<inlineCode parentName="p">{`refresh_token`}</inlineCode>{` in your response.`}</p>
    <h4 {...{
      "id": "body-parameters"
    }}>{`Body parameters`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Body parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Value`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Comment`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`grant_type`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`authorization_code`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The type of grant Plaid is exchanging for an access token. (In this case, an authorization code.)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`redirect_uri`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`https://cdn.plaid.com/link/v2/stable/oauth.html`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Where Plaid expects your organization to redirect back to once the user completed all authentication steps.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`code`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<inlineCode parentName="td">{`code`}</inlineCode>{` you sent to Plaid in the `}<a parentName="td" {...{
              "href": "#you-give-plaid-an-authorization-code"
            }}>{`previous step`}</a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The temporary authorization code Plaid is exchanging for the access token.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`code_verifier`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`(OPTIONAL) `}<inlineCode parentName="td">{`code`}</inlineCode>{` used to generate challenge`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A code provided to Plaid or generated by Plaid to use as the key for PKCE challenge codes`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "token-request-example"
    }}>{`Token request example`}</h4>
    <p>{`Plaid's request:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false # CURL example",
        "switcher": "false",
        "#": true,
        "CURL": true,
        "example": true
      }}>{`curl --request POST 'https://auth.firstplatypusbank.com/oauth2/v1/token' \\
--header 'Content-Type: application/json' \\
--header "Authorization: Basic YzVhNTI0NWIwNjJiZjg0MjBkMTFhYjQzNjFiMjhhMTU6clZYWU9vUVM0ckhVRzc5bl80OGFs"
--data-raw '{
    "grant_type": "authorization_code",
    "code": "1284918391",
    "redirect_url": "https://cdn.plaid.com/link/v2/stable/oauth.html"
}'
`}</code></pre>
    <h4 {...{
      "id": "response-parameters"
    }}>{`Response parameters`}</h4>
    <p>{`Your organization validates that the `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`client_secret`}</inlineCode>{`, `}<inlineCode parentName="p">{`code`}</inlineCode>{` and `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` parameters from Plaid's request all match the expected values. Your response to this request contains everything that Plaid needs to later access your Plaid Exchange API:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`access_token`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An opaque string (likely a JWT structured according to the `}<a parentName="td" {...{
              "href": "https://datatracker.ietf.org/doc/html/rfc9068#section-2.2"
            }}>{`JWT profile for OAuth 2.0 access tokens`}</a>{`). Plaid will present this string as a bearer token to all requests made to your Plaid Exchange API. This encodes the identity of the user and the scope of access granted.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`id_token`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An `}<a parentName="td" {...{
              "href": "https://openid.net/specs/openid-connect-core-1_0.html#IDToken"
            }}>{`OIDC ID token`}</a>{`. Plaid only reads the `}<inlineCode parentName="td">{`sub`}</inlineCode>{` field from this token.`}<br />{`In a deployment with multiple financial institutions, the `}<inlineCode parentName="td">{`sub`}</inlineCode>{` field must be unique to each financial institution. (It doesn't need to be unique to the user across all financial institutions.)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`refresh_token`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An opaque string (likely a JWT) that can be used to request a new access token. Plaid will use this to fetch data periodically long after the original access token expires. See the `}<a parentName="td" {...{
              "href": "#refresh-flow"
            }}>{`refresh flow`}</a>{` section for more information. `}<br />{`Plaid doesn't recommend setting an inactivity timeout for the refresh token because it's not a good indicator of whether a user is active with a Plaid partner. For example, some Plaid partners can trigger Plaid to call the Plaid Exchange endpoint `}<inlineCode parentName="td">{`/accounts/{accountID}`}</inlineCode>{` infrequently for large transactions.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`user_id`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`(Optional if `}<inlineCode parentName="td">{`id_token`}</inlineCode>{` is not possible) A unique identifier that replaces the `}<inlineCode parentName="td">{`id_token.sub`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "token-response-example"
    }}>{`Token response example`}</h4>
    <p>{`Your response:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "access_token": "agstynmdygjdghabrgraeh...",
  "id_token": "snsyjrhvjdtvyjvsgcegaethstj...",
  "user_id": "2347456437346745", // Optional, see above
  "refresh_token": "dhcsrtjsrgayvkdisfdgntshstu..."
}
`}</code></pre>
    <p>{`If `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`id_token`}</inlineCode>{` are not present, the final attempt to resolve the unique consistency key will be via the FDX endpoint called `}<inlineCode parentName="p">{`/customer/current`}</inlineCode>{`. The response is listed below.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "customerId": "2347456437346745"
}
`}</code></pre>
    <p>{`After it receives this response, Plaid has everything it needs to access your Plaid Exchange API.`}</p>
    <h2 {...{
      "id": "refresh-flow"
    }}>{`Refresh flow`}</h2>
    <p>{`For some use cases, Plaid needs to periodically fetch fresh data on behalf of the user. To get a new access token, Plaid makes another request to your `}<inlineCode parentName="p">{`token_endpoint`}</inlineCode>{` with a different set of parameters.`}</p>
    <h4 {...{
      "id": "body-parameters-1"
    }}>{`Body parameters`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Body parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Value`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`grant_type`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`refresh_token`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Specifies that Plaid is requesting a new access token to replace the expired access token.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`refresh_token`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Example: `}<inlineCode parentName="td">{`dhcsrtjsrgayvkdisfdgntshstu...`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The refresh token you issued to Plaid. `}<br /><br />{` `}<strong parentName="td">{`Note:`}</strong>{` Plaid recommends setting the expiration at 13 months. This allows you to avoid running into expiration issues during time-sensitive intervals (for example, on tax day).`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "refresh-example"
    }}>{`Refresh example`}</h4>
    <p>{`Plaid's request:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false # CURL example",
        "switcher": "false",
        "#": true,
        "CURL": true,
        "example": true
      }}>{`curl --request POST 'https://auth.firstplatypusbank.com/oauth2/v1/token' \\
--user "plaid:rVXYOoQS4rHUG79n_48al"
--header 'Content-Type: application/json' \\
--data-raw '{
    "grant_type": "refresh_token",
    "refresh_token": "dhcsrtjsrgayvkdisfdgntshstu..."
}'
`}</code></pre>
    <p>{`Your response:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "access_token": "lngarogglkcangasgabba...",
  "expires_in": 900,
  "id_token": "snsyjrhvjdtvyjvsgcegaethstj..."
}
`}</code></pre>
    <p>{`See the `}<a parentName="p" {...{
        "href": "#response-parameters"
      }}>{`previous section`}</a>{` for descriptions of these response parameters.`}</p>
    <h2 {...{
      "id": "error-handling"
    }}>{`Error handling`}</h2>
    <p>{`How to `}<a parentName="p" {...{
        "href": "https://www.rfc-editor.org/rfc/rfc6749#section-4.1.2.1"
      }}>{`handle an error`}</a>{` that occurs during the `}<a parentName="p" {...{
        "href": "#authorization-flow"
      }}>{`authorization flow`}</a>{`.`}</p>
    <h4 {...{
      "id": "incorrect-redirect-uri"
    }}>{`Incorrect redirect URI`}</h4>
    <p>{`If the request fails due to an incorrect, missing, invalid, or mismatched `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{`,
notify Plaid of the error and do not redirect the user to the `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{`.
We recommend displaying an error page to notify the user that an error has occurred.`}</p>
    <h4 {...{
      "id": "user-cancellation"
    }}>{`User cancellation`}</h4>
    <p>{`If the user cancels the request or if the request fails for any other reason other than an incorrect URI,
include the following required query parameters with the request.
(Please see the `}<a parentName="p" {...{
        "href": "https://www.rfc-editor.org/rfc/rfc6749#section-4.1.2.1"
      }}>{`OAuth spec`}</a>{` for a complete list of possible parameters.)`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Query parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`error`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The reason for the error. See the `}<a parentName="td" {...{
              "href": "#errors"
            }}>{`Errors`}</a>{` table below for a list of possible errors.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`state`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The opaque string Plaid passed as the `}<inlineCode parentName="td">{`state`}</inlineCode>{` parameter in the `}<inlineCode parentName="td">{`authorization_endpoint`}</inlineCode>{` redirect step.`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "errors"
    }}>{`Errors`}</h4>
    <p>{`See the table below for a full list of possible errors, as defined in the `}<a parentName="p" {...{
        "href": "https://www.rfc-editor.org/rfc/rfc6749#section-4.1.2.1"
      }}>{`OAuth spec`}</a>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Parameter`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`invalid_request`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The request is missing a required parameter, includes an invalid parameter value, includes a parameter more than once, or is otherwise malformed.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`unauthorized_client`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The client is not authorized to request an authorization code using this method.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`access_denied`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The resource owner or authorization server denied the request.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`unsupported_response_type`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The authorization server does not support obtaining an authorization code using this method.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`invalid_scope`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The requested scope is invalid, unknown, or malformed.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`server_error`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The authorization server encountered an unexpected condition that prevented it from fulfilling the request. (This error code is needed because a 500 Internal Server Error HTTP status code cannot be returned to the client via an HTTP redirect.)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`temporarily_unavailable`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The authorization server is currently unable to handle the request due to a temporary overloading or maintenance of the server. (This error code is needed because a 503 Service Unavailable HTTP status code can't be returned to the client via an HTTP redirect.)`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "example-1"
    }}>{`Example`}</h4>
    <p>{`Your response:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false # CURL example",
        "switcher": "false",
        "#": true,
        "CURL": true,
        "example": true
      }}>{`https://cdn.plaid.com/link/v2/stable/oauth.html?error=access_denied&state=eyJvYXV0aF9zdGF0ZV
`}</code></pre>
    <h2 {...{
      "id": "alternative-auth-methods"
    }}>{`Alternative auth methods`}</h2>
    <Callout warning mdxType="Callout">
      <p>{`Plaid provides legacy support for existing integrations using non-OAuth authentication methods. However, these methods are deprecated, and new PX integrations must use OAuth for authentication.`}</p>
    </Callout>
    <p><a parentName="p" {...{
        "href": "/plaid-exchange/docs/authentication"
      }}>{`OAuth`}</a>{` is the preferred security method. However there are many existing integrations using the MFA/OTP options for authenticating a user. Plaid will continue to support these methods, but they are deprecated for new users.`}</p>
    <p>{`Please work with Plaid if you have a limitation to OAuth and must use OTP or MFA.`}</p>
    <p>{`This API reference describes the following alternative security endpoints with the following workflow:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "#request-an-auth-token-for-a-user"
          }}>{`POST /users/auth_token`}</a>{`:
this endpoint is used for basic as well as multi-factor authentication.`}</p>
        <ul parentName="li">
          <li parentName="ul">{`For basic, it returns an authorization response with an auth token for the user.`}</li>
          <li parentName="ul">{`For multifactor authentication flows, the endpoint responds with one of the following:`}
            <ul parentName="li">
              <li parentName="ul"><a parentName="li" {...{
                  "href": "#202-response-for-otp"
                }}>{`OTP challenge response`}</a></li>
              <li parentName="ul"><a parentName="li" {...{
                  "href": "#202-response-for-kba"
                }}>{`KBA challenge response`}</a></li>
              <li parentName="ul"><a parentName="li" {...{
                  "href": "#202-response-for-totp"
                }}>{`TOTP challenge response`}</a></li>
            </ul>
          </li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Based on the multifactor challenge type, Plaid then calls one or both of the following:`}</p>
        <ul parentName="li">
          <li parentName="ul"><a parentName="li" {...{
              "href": "#trigger-otp"
            }}>{`POST /users/{user_id}/sendOtp`}</a>{`: Plaid sends this for `}<inlineCode parentName="li">{`OTP`}</inlineCode>{` flows only.`}</li>
          <li parentName="ul"><a parentName="li" {...{
              "href": "#validate-2fa"
            }}>{`POST /users/{user_id}/2fa`}</a>{`
For this endpoint, depending on the multi-factor authentication flow, Plaid sends one of the following:`}
            <ul parentName="li">
              <li parentName="ul"><a parentName="li" {...{
                  "href": "#validate-totp-or-otp"
                }}>{`OTP validation request`}</a>{`: For `}<inlineCode parentName="li">{`OTP`}</inlineCode>{` or `}<inlineCode parentName="li">{`TOTP`}</inlineCode>{`.`}</li>
              <li parentName="ul"><a parentName="li" {...{
                  "href": "#validate-kba"
                }}>{`KBA validation request`}</a>{`: For `}<inlineCode parentName="li">{`KBA`}</inlineCode>{`.
If the validation is successful, this endpoint returns an authorization response with an auth token for the user.`}</li>
            </ul>
          </li>
        </ul>
      </li>
    </ol>
    <h4 {...{
      "id": "request-an-auth-token-for-a-user"
    }}>{`Request an auth token for a user`}</h4>
    <p>{`Plaid uses the `}<inlineCode parentName="p">{`POST /users/auth_token`}</inlineCode>{` endpoint to request an auth token for a user. This endpoint is the essential API method for authenticating user credentials and is a part of all non-OAuth authentication flows documented here, including multifactor flows.`}</p>
    <ul>
      <li parentName="ul">{`For an overview of the basic flow of this endpoint, see `}<a parentName="li" {...{
          "href": "#alternative-auth-methods"
        }}>{`Alternative auth methods`}</a>{`.`}</li>
      <li parentName="ul">{`For an example request and response, see the following section:`}</li>
    </ul>
    <Layout.SideBySide>
      <AggregatorSchema method='POST' route='/users/auth_token' type='request' mdxType="AggregatorSchema" />
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "switcher=false # CURL example",
          "switcher": "false",
          "#": true,
          "CURL": true,
          "example": true
        }}>{`curl  --location --request POST
'https://your-institution.com/users/auth_token/' \\
--header 'Content-Type: application/x-www-form-urlencoded' \\
--header 'X-PLAID-CLIENT-ID: example_ID' \\
--header 'X-PLAID-SECRET: example_secret' \\
--header 'X-PLAID-VERSION: 2021-03-26' \\
--header 'Accept: application/json' \\
--data-urlencode 'username=user123&password=pass123&institution_id=inst123'
`}</code></pre>
    </Layout.SideBySide>
    <h5 {...{
      "id": "responses"
    }}>{`Responses`}</h5>
    <p>{`Successful responses include `}<inlineCode parentName="p">{`200`}</inlineCode>{` for basic authentication and `}<inlineCode parentName="p">{`202`}</inlineCode>{` for multifactor authentication.`}</p>
    <h6 {...{
      "id": "200-ok"
    }}>{`200 OK`}</h6>
    <p>{`For basic authentication, `}<inlineCode parentName="p">{`POST users/auth_token`}</inlineCode>{` returns a 200 response with the access token:`}</p>
    <AggregatorSchema method='POST' route='/users/auth_token' type='response' exampleTitle='200 Example AuthenticationResponse' mdxType="AggregatorSchema" />
    <h6 {...{
      "id": "202-accepted"
    }}>{`202 Accepted`}</h6>
    <p>{`For more information, see the following `}<a parentName="p" {...{
        "href": "#multifactor-authentication"
      }}>{`multifactor authentication section`}</a>{`.`}</p>
    <h6 {...{
      "id": "400-bad-request"
    }}>{`400 Bad Request`}</h6>
    <AggregatorSchema type='model' model='BasicErrorResponse' tableOnly={true} mdxType="AggregatorSchema" />
    <h6 {...{
      "id": "401-not-authorized"
    }}>{`401 Not Authorized`}</h6>
    <AggregatorSchema type='model' model='BasicErrorResponse' tableOnly={true} mdxType="AggregatorSchema" />
    <h6 {...{
      "id": "403-forbidden"
    }}>{`403 Forbidden`}</h6>
    <p>{`The user’s account is locked, typically due to excessive incorrect authentication attempts. This response will trigger messaging to the user indicating that the account has been temporarily locked and will advise the user to contact the institution.`}</p>
    <h6 {...{
      "id": "405-not-allowed"
    }}>{`405 Not Allowed`}</h6>
    <p>{`This response should be used to indicate that the user’s account is not permitted to participate in aggregation, typically because the user must accept a license or terms of use. This will trigger messaging to the user indicating that the account is not yet authorized for online use and will direct the user to visit the partner institution’s online portal for further guidance.
Plaid recommends that whatever pending terms or agreements that block this use case be presented to the user immediately upon login.`}</p>
    <h6 {...{
      "id": "503"
    }}>{`503`}</h6>
    <AggregatorSchema type='model' model='InstitutionErrorResponse' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "multifactor-authentication"
    }}>{`Multifactor authentication`}</h4>
    <p>{`If the response from `}<inlineCode parentName="p">{`POST /users/auth_token`}</inlineCode>{` is `}<inlineCode parentName="p">{`202 Accepted`}</inlineCode>{`, then Plaid must challenge the user with a multifactor authentication flow.`}</p>
    <p>{`The 202 response indicates that presented credentials are accepted for further processing. It does not indicate that the presented credentials are correct.
For an overview of the multifactor authentication flow, see `}<a parentName="p" {...{
        "href": "#alternative-auth-methods"
      }}>{`Alternative auth methods`}</a>{`.`}</p>
    <p>{`The 202 response body is one of the following:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`MfaOtpEscalationChallenge`}</inlineCode>{` for temporary, out-of-band password 2FA authentication. This indicates Plaid next requests `}<a parentName="li" {...{
          "href": "#trigger-otp"
        }}><inlineCode parentName="a">{`POST /users/{user_id}/sendOtp`}</inlineCode></a>{` then `}<a parentName="li" {...{
          "href": "#validate-2fa"
        }}><inlineCode parentName="a">{`POST /users/{user_id}/2fa`}</inlineCode></a>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`MfaKbaEscalationChallenge`}</inlineCode>{` for knowledge-based 2FA authentication. This indicates Plaid next requests `}<a parentName="li" {...{
          "href": "#validate-2fa"
        }}><inlineCode parentName="a">{`POST /users/{user_id}/2fa`}</inlineCode></a>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`MfaTotpEscalationChallenge`}</inlineCode>{` for temporary, on-hand password 2FA authentication. This indicates Plaid next requests `}<a parentName="li" {...{
          "href": "#validate-2fa"
        }}><inlineCode parentName="a">{`POST /users/{user_id}/2fa`}</inlineCode></a>{`.`}</li>
    </ul>
    <p>{`See the following sections for details.`}</p>
    <h4 {...{
      "id": "202-response-for-otp"
    }}>{`202 response for OTP`}</h4>
    <p>{`If Plaid receives the `}<inlineCode parentName="p">{`MfaOtpEscalationChallenge`}</inlineCode>{` response (the most common scenario) from `}<inlineCode parentName="p">{`POST /users/auth_token`}</inlineCode>{`, Plaid:`}</p>
    <ol>
      <li parentName="ol">{`Prompts the user in Plaid Link to select how they want their OTP sent, displaying the methods the partner indicates it supports in the `}<inlineCode parentName="li">{`MfaOtpEscalationChallenge`}</inlineCode>{` response. For example, `}<inlineCode parentName="li">{`voice`}</inlineCode>{` or `}<inlineCode parentName="li">{`sms`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Sends the `}<inlineCode parentName="li">{`POST /users/{user_id}/sendOtp`}</inlineCode>{` request indicating the partner should send the OTP to the user's selected method. For more information see `}<a parentName="li" {...{
          "href": "#trigger-otp"
        }}>{`Trigger OTP API method`}</a>{`.`}</li>
      <li parentName="ol">{`After receiving a 200 response, prompts the user in Plaid Link to fill in the OTP and waits for them to fill it in.`}</li>
      <li parentName="ol">{`Sends the OTP the user entered for validation using the `}<inlineCode parentName="li">{`POST /users/{user_id}/2fa`}</inlineCode>{` request. For more information see `}<a parentName="li" {...{
          "href": "#validate-2fa"
        }}>{`Validate 2FA`}</a>{`.`}</li>
    </ol>
    <h5 {...{
      "id": "mfaotpescalationchallenge-response"
    }}>{`MfaOtpEscalationChallenge response`}</h5>
    <AggregatorSchema type='model' model='MfaOtpEscalationChallengeResponse' exampleTitle='202 MfaOtpEscalationChallenge example response' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "202-response-for-kba"
    }}>{`202 response for KBA`}</h4>
    <p>{`If Plaid receives the `}<inlineCode parentName="p">{`MfaKbaEscalationChallenge`}</inlineCode>{` response from `}<inlineCode parentName="p">{`POST /users/auth_token`}</inlineCode>{`, Plaid:`}</p>
    <ol>
      <li parentName="ol">{`Prompts the user in Plaid Link to enter answers to the questions that Plaid received in the `}<inlineCode parentName="li">{`MfaKbaEscalationChallenge`}</inlineCode>{` response.`}</li>
      <li parentName="ol">{`Sends the answers the user entered for validation using the `}<inlineCode parentName="li">{`POST /users/{user_id}/2fa`}</inlineCode>{` request. For more information see `}<a parentName="li" {...{
          "href": "#validate-2fa"
        }}>{`Validate 2FA`}</a>{`.`}</li>
    </ol>
    <h5 {...{
      "id": "mfakbaescalationchallenge-response"
    }}>{`MfaKbaEscalationChallenge response`}</h5>
    <AggregatorSchema type='model' model='MfaKbaEscalationChallengeResponse' exampleTitle='202 MfaKbaEscalationChallenge example response' mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "202-response-for-totp"
    }}>{`202 response for TOTP`}</h4>
    <p>{`If Plaid receives the `}<inlineCode parentName="p">{`MfaTotpEscalationChallenge`}</inlineCode>{` response from `}<inlineCode parentName="p">{`POST /users/auth_token`}</inlineCode>{`,
this indicates that the partner doesn't have to send
a temporary password because the user already has the temporary password generator on hand, for example in the form of an
authenticator app that displays a new passcode every 60 seconds. If Plaid receives this response, Plaid:`}</p>
    <ol>
      <li parentName="ol">{`Prompts the user in Plaid Link to fill in the TOTP and waits for them to fill it in.`}</li>
      <li parentName="ol">{`Sends the TOTP the user entered for validation using the `}<inlineCode parentName="li">{`POST /users/{user_id}/2fa`}</inlineCode>{` request. For more information see `}<a parentName="li" {...{
          "href": "#validate-2fa"
        }}>{`Validate 2FA`}</a>{`.`}</li>
    </ol>
    <h5 {...{
      "id": "mfatotpescalationchallenge-response"
    }}>{`MfaTotpEscalationChallenge response`}</h5>
    <AggregatorSchema type='model' model='MfaTotpEscalationChallengeResponse' exampleTitle='202 MfaTotpEscalationChallenge response' mdxType="AggregatorSchema" />
    <h6 {...{
      "id": "401-unauthorized"
    }}>{`401 Unauthorized`}</h6>
    <AggregatorSchema type='model' model='BasicErrorResponse' tableOnly={true} mdxType="AggregatorSchema" />
    <h6 {...{
      "id": "403-forbidden-1"
    }}>{`403 Forbidden`}</h6>
    <p>{`This response indicates the user’s account is locked, typically due to excessive incorrect authentication attempts. This response will trigger messaging to the user indicating that the account has been temporarily locked, and will advise the user to contact the institution.`}</p>
    <h6 {...{
      "id": "405-not-allowed-1"
    }}>{`405 Not Allowed`}</h6>
    <p>{`This response indicates that the user’s account is not permitted to participate in aggregation, typically because the user must accept a license or terms of use. This will trigger messaging to the user indicating that the account is not yet authorized for online use, and will direct the user to visit the partner institution’s online portal for further guidance.`}</p>
    <p>{`Plaid recommends that whatever pending terms or agreements that block this use case be presented to the user immediately upon login.`}</p>
    <h4 {...{
      "id": "trigger-otp"
    }}>{`Trigger OTP`}</h4>
    <Layout.SideBySide>
      <AggregatorSchema type='request' method='POST' route='/users/{user_id}/sendOtp' exampleTitle='/users/{user_id}/sendOtp' mdxType="AggregatorSchema" />
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "switcher=false # CURL example for out-of-band OTP flow",
          "switcher": "false",
          "#": true,
          "CURL": true,
          "example": true,
          "for": true,
          "out-of-band": true,
          "OTP": true,
          "flow": true
        }}>{`curl -X POST 'https://your-institution.com/users/example_user_id_1/sendOtp' \\
    --header'X-PLAID-CLIENT-ID: PLAID' \\
    --header 'X-PLAID-SECRET: example_secret' \\
    --header 'X-PLAID-VERSION: 2021-03-26' \\
    --header 'Accept: application/json'  \\
    --header 'Content-Type: application/x-www-form-urlencoded' \\
    --data-urlencode 'challenge_id=36b4Xo&send_method_id=Z9D0iK' \\
`}</code></pre>
    </Layout.SideBySide>
    <h5 {...{
      "id": "responses-1"
    }}>{`Responses`}</h5>
    <h6 {...{
      "id": "200-ok-1"
    }}>{`200 OK`}</h6>
    <p>{`This response indicates that the send method was acceptable and the partner institution will transmit the passcode to the user. No response body is required.`}</p>
    <h6 {...{
      "id": "400-bad-request-1"
    }}>{`400 Bad Request`}</h6>
    <AggregatorSchema type='model' model='BasicErrorResponse' tableOnly={true} mdxType="AggregatorSchema" />
    <h6 {...{
      "id": "503-1"
    }}>{`503`}</h6>
    <AggregatorSchema type='model' model='InstitutionErrorResponse' tableOnly={true} mdxType="AggregatorSchema" />
    <h4 {...{
      "id": "validate-2fa"
    }}>{`Validate 2FA`}</h4>
    <h5 {...{
      "id": "post-usersuser_id2fa"
    }}>{`POST /users/{user_id}/2fa`}</h5>
    <p><inlineCode parentName="p">{`POST /users/{user_id}/2fa`}</inlineCode>{` receives and validates the user's response to the escalation challenge.
This is the final endpoint Plaid request in all multi-factor flow types.`}</p>
    <p>{`Plaid sends one of the following request bodies to this endpoint:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`ValidateOtpChallengeRequest`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`ValidateKbaChallengeRequest`}</inlineCode></li>
    </ul>
    <p>{`See the following sections for details.`}</p>
    <h4 {...{
      "id": "validate-totp-or-otp"
    }}>{`Validate TOTP or OTP`}</h4>
    <h6 {...{
      "id": "validateotpchallengerequest"
    }}>{`ValidateOtpChallengeRequest`}</h6>
    <Layout.SideBySide>
      <AggregatorSchema type='model' model='ValidateOtpChallengeRequest' mdxType="AggregatorSchema" />
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "switcher=false # POST /users/{user_id}/2fa/ with ValidateOtpChallengeRequest body",
          "switcher": "false",
          "#": true,
          "POST": true,
          "/users/{user_id}/2fa/": true,
          "with": true,
          "ValidateOtpChallengeRequest": true,
          "body": true
        }}>{`curl  --location --request POST
'https://your-institution.com/users/user123/2fa/' \\
--header 'Content-Type: application/x-www-form-urlencoded' \\
--header 'X-PLAID-CLIENT-ID: example_ID' \\
--header 'X-PLAID-SECRET: example_secret' \\
--header 'X-PLAID-VERSION: 2021-03-26' \\
--header 'Accept: application/json' \\
--data-urlencode 'passcode=123456&challenge_id=example_id'
`}</code></pre>
    </Layout.SideBySide>
    <h4 {...{
      "id": "validate-kba"
    }}>{`Validate KBA`}</h4>
    <h6 {...{
      "id": "validatekbachallengerequest"
    }}>{`ValidateKbaChallengeRequest`}</h6>
    <Layout.SideBySide>
      <AggregatorSchema type='model' model='ValidateKbaChallengeRequest' mdxType="AggregatorSchema" />
      <pre><code parentName="pre" {...{
          "className": "language-bash",
          "metastring": "switcher=false # POST /users/{user_id}/2fa/ with ValidateKbaChallengeRequest body",
          "switcher": "false",
          "#": true,
          "POST": true,
          "/users/{user_id}/2fa/": true,
          "with": true,
          "ValidateKbaChallengeRequest": true,
          "body": true
        }}>{`curl  --location --request POST 'https://your-institution.com/users/user123/2fa/' \\
--header 'Content-Type: application/x-www-form-urlencoded' \\
--header 'X-PLAID-CLIENT-ID: example_ID' \\
--header 'X-PLAID-SECRET: example_secret' \\
--header 'X-PLAID-VERSION: 2021-03-26' \\
--header 'Accept: application/json' \\
--data-urlencode 'challenge_id=string' \\
--data-urlencode 'answers[0].question_id=string' \\
--data-urlencode 'answers[0].text=San Francisco' \\
--data-urlencode 'answers[1].question_id=string2' \\
--data-urlencode 'answers[1].text=Billings High School'
`}</code></pre>
    </Layout.SideBySide>
    <h5 {...{
      "id": "responses-2"
    }}>{`Responses`}</h5>
    <h6 {...{
      "id": "200-ok-2"
    }}>{`200 OK`}</h6>
    <p>{`The challenge response was correct. The partner returns the access token in the `}<inlineCode parentName="p">{`AuthenticationResponse`}</inlineCode>{`.`}</p>
    <AggregatorSchema method='POST' route='/users/{user_id}/2fa' type='response' exampleTitle='200 Example AuthenticationResponse' mdxType="AggregatorSchema" />
    <h6 {...{
      "id": "400-bad-request-2"
    }}>{`400 Bad Request`}</h6>
    <AggregatorSchema type='model' model='BasicErrorResponse' tableOnly={true} mdxType="AggregatorSchema" />
    <h6 {...{
      "id": "401-not-authorized-1"
    }}>{`401 Not Authorized`}</h6>
    <AggregatorSchema type='model' model='BasicErrorResponse' tableOnly={true} mdxType="AggregatorSchema" />
    <h6 {...{
      "id": "503-2"
    }}>{`503`}</h6>
    <AggregatorSchema type='model' model='InstitutionErrorResponse' tableOnly={true} mdxType="AggregatorSchema" />
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 2,
        title: "Overview",
      },
{
        id: "prepare-your-oauth-20-server",
        level: 3,
        title: "Prepare your OAuth 2.0 server",
      },
{
        id: "client-secret",
        level: 4,
        title: "Client secret",
      },
{
        id: "how-to-generate-a-client-secret",
        level: 4,
        title: "How to generate a client secret",
      },
{
        id: "authorization-flow",
        level: 2,
        title: "Authorization flow",
      },
{
        id: "plaid-redirects-the-end-user",
        level: 4,
        title: "Plaid redirects the end user",
      },
{
        id: "example",
        level: 4,
        title: "Example",
      },
{
        id: "you-give-plaid-an-authorization-code",
        level: 4,
        title: "You give Plaid an authorization code",
      },
{
        id: "auth-example",
        level: 4,
        title: "Auth Example",
      },
{
        id: "token-flow",
        level: 2,
        title: "Token Flow",
      },
{
        id: "body-parameters",
        level: 4,
        title: "Body parameters",
      },
{
        id: "token-request-example",
        level: 4,
        title: "Token request example",
      },
{
        id: "response-parameters",
        level: 4,
        title: "Response parameters",
      },
{
        id: "token-response-example",
        level: 4,
        title: "Token response example",
      },
{
        id: "refresh-flow",
        level: 2,
        title: "Refresh flow",
      },
{
        id: "body-parameters-1",
        level: 4,
        title: "Body parameters",
      },
{
        id: "refresh-example",
        level: 4,
        title: "Refresh example",
      },
{
        id: "error-handling",
        level: 2,
        title: "Error handling",
      },
{
        id: "incorrect-redirect-uri",
        level: 4,
        title: "Incorrect redirect URI",
      },
{
        id: "user-cancellation",
        level: 4,
        title: "User cancellation",
      },
{
        id: "errors",
        level: 4,
        title: "Errors",
      },
{
        id: "example-1",
        level: 4,
        title: "Example",
      },
{
        id: "alternative-auth-methods",
        level: 2,
        title: "Alternative auth methods",
      },
{
        id: "request-an-auth-token-for-a-user",
        level: 4,
        title: "Request an auth token for a user",
      },
{
        id: "multifactor-authentication",
        level: 4,
        title: "Multifactor authentication",
      },
{
        id: "202-response-for-otp",
        level: 4,
        title: "202 response for OTP",
      },
{
        id: "202-response-for-kba",
        level: 4,
        title: "202 response for KBA",
      },
{
        id: "202-response-for-totp",
        level: 4,
        title: "202 response for TOTP",
      },
{
        id: "trigger-otp",
        level: 4,
        title: "Trigger OTP",
      },
{
        id: "validate-2fa",
        level: 4,
        title: "Validate 2FA",
      },
{
        id: "validate-totp-or-otp",
        level: 4,
        title: "Validate TOTP or OTP",
      },
{
        id: "validate-kba",
        level: 4,
        title: "Validate KBA",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
